import { useEffect, useMemo, useState } from 'react';

import Button from '@lib/core/src/components/Button';
import SearchInput from '@lib/core/src/components/SearchInput';
import { Skeleton, Stack } from '@mui/material';

import TranscriptRow from './TranscriptRow';
import {
  Container,
  Row,
  Title,
  TranscriptHeader,
  TranscriptList,
  TranscriptListBlock
} from './TranscriptStyles';
import { TranscriptEntry, TranscriptEntryWithId } from './types';

const Transcript = ({
  meetingTranscriptUrl,
  transcriptContainerRef,
  markedId,
  onTimeClicked,
  setVideoParts,
  setIsScrolling
}: {
  meetingTranscriptUrl: string | undefined;
  transcriptContainerRef: React.RefObject<HTMLUListElement>;
  markedId: string;
  onTimeClicked: (time: number) => void;
  setVideoParts: (parts: Record<string, string>) => void;
  setIsScrolling: React.Dispatch<React.SetStateAction<boolean>>;
}) => {
  const [filter, setFilter] = useState<string>('');
  const [transcript, setTranscript] = useState<TranscriptEntryWithId[]>();
  const [isSyncButtonDisabled, setIsSyncButtonDisabled] = useState<boolean>(true);

  useEffect(() => {
    if (!meetingTranscriptUrl) return;
    fetch(meetingTranscriptUrl, {
      method: 'GET'
    })
      .then(response => response.json())
      .then(data => {
        const dataWithIds = data.map((entry: TranscriptEntry) => ({
          ...entry,
          id: crypto.randomUUID()
        })) as (TranscriptEntry & { id: string })[];
        setTranscript(dataWithIds);
        const parts: Record<string, string> = {};
        dataWithIds.forEach(entry => {
          const key = entry.words[0].start_timestamp | 0;
          parts[key] = entry.id;
        });
        setVideoParts(parts);
      });
  }, [meetingTranscriptUrl]);

  useEffect(() => {
    if (!transcriptContainerRef.current) return;
    const transcriptContainerNode = transcriptContainerRef.current;
    transcriptContainerNode?.addEventListener('scroll', onTranscriptScrollPositionDetected);

    return () => {
      transcriptContainerNode?.removeEventListener('scroll', onTranscriptScrollPositionDetected);
    };
  }, [transcriptContainerRef.current, markedId]);

  const onTranscriptScrollPositionDetected = (event: Event) => {
    const target = event.target as HTMLDivElement;
    const containerRect = target.getBoundingClientRect();
    const itemRect = document.getElementById(markedId)?.getBoundingClientRect();

    if (itemRect) {
      const isVisible =
        itemRect?.top >= containerRect.top && itemRect?.bottom <= containerRect.bottom;
      setIsSyncButtonDisabled(isVisible);
    }
  };

  const onSyncButtonClicked = () => {
    setIsScrolling(false);
    document.getElementById(markedId)?.scrollIntoView({ block: 'center', behavior: 'smooth' });
  };

  const transcriptList = useMemo(() => {
    if (!transcript) return [];
    return transcript.filter(
      entry =>
        entry?.words?.[0]?.text?.toLowerCase().includes(filter.toLowerCase()) || filter === ''
    );
  }, [transcript, filter]);

  if (!meetingTranscriptUrl) {
    return (
      <Container>
        {Array.from({ length: 7 }).map((_, index) => (
          <Skeleton
            key={index}
            sx={{
              borderRadius: '30px'
            }}
            variant="rounded"
            width="100%"
            height="24px"
            animation="wave"
          />
        ))}
      </Container>
    );
  }

  return (
    <Container>
      <TranscriptHeader>
        <Row>
          <Title>Transcript</Title>
          {!isSyncButtonDisabled && (
            <Button onClick={onSyncButtonClicked} text={'Sync with Audio'} size="small"></Button>
          )}
        </Row>
        <SearchInput placeholder="Search" onChange={value => setFilter(value)} value={filter} />
      </TranscriptHeader>
      <TranscriptListBlock>
        {transcript && transcript.length > 0 ? (
          <TranscriptList id="transcript" ref={transcriptContainerRef}>
            {transcriptList.map(entry => (
              <TranscriptRow
                key={entry.id}
                entry={entry}
                onTimeClicked={onTimeClicked}
                markedId={markedId}
              />
            ))}
          </TranscriptList>
        ) : (
          <Stack>No transcript available</Stack>
        )}
      </TranscriptListBlock>
    </Container>
  );
};

export default Transcript;
